/**
 * Planos antigos (spec 057; Vitor, 19/10): os planos que o cliente teve na plataforma antiga e nao constam aqui.
 *
 * - `PaginaAdicionarPlanosAntigos` (ficha do cliente > Adicionar plano > Vários planos antigos): uma planilha, uma linha
 *   por plano, ativo ou encerrado, com as datas de cada etapa e a conta. Uma pergunta so pro grupo: avisar o cliente?
 *   O grupo grava inteiro ou nada; a linha com problema fica marcada com o motivo.
 * - `PaginaPlanosAntigos` (Cadastros > Planos antigos): a lista editavel dos ja adicionados, sem avisar o cliente.
 * - `EditarPlanoAntigo` e `ExcluirPlanoAntigo` (ficha do cliente, Vitor 23/10): a mesma correcao e a mesma exclusao da
 *   lista, num dialogo, nas linhas de origem ANTIGO da tabela de planos.
 *
 * O que cada linha pede depende do plano, da situacao e da etapa (o contexto vem do backend): so as etapas percorridas
 * tem inicio; prazo do teste so no ativo no Teste; proximo vencimento so no ativo numa etapa que paga mensalidade.
 */
import {
  Aviso, Botao, CabecalhoDePagina, Campo, CampoData, CampoSelecao, CampoTextoLongo, Carregando, EntradaDaPlanilha, ErroApi, LinkInterno, Pilha, Planilha, Segmentado, SelecaoDaPlanilha, Selo, Vazio, type EstadoDaLinha,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { erroDe, useApi } from "../api";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";
import { ForaDaPagina } from "./ForaDaPagina";

type Etapa = "TESTE" | "SIMULADOR" | "MESA_REAL";
type Plataforma = "PROFIT_ONE" | "PROFIT_PRO";
type Situacao = "ATIVO" | "ENCERRADO";
type Inicios = Partial<Record<Etapa, string>>;
type Contexto = {
  hoje: string;
  planos: { slug: string; nome: string; ativo: boolean; etapas: { codigo: Etapa; rotulo: string }[] }[];
  mesas: Record<Etapa, { conta: string; nome: string; ativa: boolean }[]>;
  motivos: { codigo: string; rotulo: string; exige_detalhe: boolean }[];
  paga: Record<Etapa, Record<Plataforma, boolean>>;
};

const ETAPAS: { codigo: Etapa; rotulo: string }[] = [{ codigo: "TESTE", rotulo: "Teste" }, { codigo: "SIMULADOR", rotulo: "Simulador" }, { codigo: "MESA_REAL", rotulo: "Mesa Real" }];
const PLATAFORMAS: { valor: Plataforma; rotulo: string }[] = [{ valor: "PROFIT_ONE", rotulo: "Profit One" }, { valor: "PROFIT_PRO", rotulo: "Profit Pro" }];
const SITUACOES: { valor: Situacao; rotulo: string }[] = [{ valor: "ATIVO", rotulo: "Ativo" }, { valor: "ENCERRADO", rotulo: "Encerrado" }];
const CONTA_EM_USO = "CONFIRMAR_CONTA_EM_USO";
const naoSeAplica = <span className="mtc-texto-2" title="Não se aplica a esta linha">-</span>;

/** `ligado`: o dialogo da ficha fica montado fechado e so busca quando abre (a ficha da Global nem tem etapas). */
function useContexto(ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["planos-antigos", "contexto"], queryFn: () => ler<Contexto>("/v1/admin/planos-antigos/contexto"), staleTime: 60_000, enabled: ligado });
}

/** As etapas do plano ate a escolhida: so elas tem inicio. */
function percorridas(c: Contexto | undefined, slug: string, etapa: Etapa | "" | null): Etapa[] {
  const ordem = c?.planos.find((p) => p.slug === slug)?.etapas.map((e) => e.codigo) ?? [];
  return etapa && ordem.includes(etapa) ? ordem.slice(0, ordem.indexOf(etapa) + 1) : [];
}
const opcoesDeMesa = (c: Contexto | undefined, etapa: Etapa | "" | null) => [
  { valor: "", rotulo: "Sem subconta" },
  ...(etapa ? (c?.mesas[etapa] ?? []).map((m) => ({ valor: m.conta, rotulo: `${m.nome}${m.ativa ? "" : " (aposentada)"}` })) : []),
];

// ------------------------------------------------------------------ adicionar (ficha do cliente)

type LinhaNova = {
  chave: string;
  plano_slug: string;
  plataforma: Plataforma | "";
  comprada_em: string;
  situacao: Situacao | "";
  etapa: Etapa | "";
  inicios: Inicios;
  subconta: string;
  mesa: string;
  confirmar_conta_em_uso: boolean;
  teste_ate: string;
  teste_sem_prazo: boolean;
  proximo_vencimento: string;
  encerrada_em: string;
  motivo_encerramento: string;
  detalhe_encerramento: string;
};
let sequencia = 0;
const linhaVazia = (): LinhaNova => ({ chave: `l${++sequencia}`, plano_slug: "", plataforma: "", comprada_em: "", situacao: "", etapa: "", inicios: {}, subconta: "", mesa: "", confirmar_conta_em_uso: false, teste_ate: "", teste_sem_prazo: false, proximo_vencimento: "", encerrada_em: "", motivo_encerramento: "", detalhe_encerramento: "" });

export function PaginaAdicionarPlanosAntigos() {
  const { id = "" } = useParams();
  const { ler, postar } = useApi();
  const navegar = useNavigate();
  const ctx = useContexto();
  const pessoa = useQuery({ queryKey: ["pessoa", id], queryFn: () => ler<{ pessoa: { id: string; nome: string } }>(`/v1/admin/pessoas/${id}`) });
  const [linhas, setLinhas] = useState<LinhaNova[]>(() => [linhaVazia()]);
  const [motivo, setMotivo] = useState("");
  const [avisar, setAvisar] = useState<"" | "sim" | "nao">("");
  const [erro, setErro] = useState<{ texto: string; linha: number | null; contaEmUso: boolean }>();
  const [salvando, setSalvando] = useState(false);
  const c = ctx.data;
  const nome = pessoa.data?.pessoa.nome ?? "o cliente";

  const mexer = (i: number, p: Partial<LinhaNova>) => {
    setErro(undefined);
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  };
  // Trocar o plano ou a etapa zera o que dependia deles (as etapas e a mesa mudam).
  const trocarPlano = (i: number, slug: string) => mexer(i, { plano_slug: slug, etapa: "", inicios: {}, mesa: "" });
  const trocarEtapa = (i: number, etapa: Etapa) => mexer(i, { etapa, mesa: "", inicios: Object.fromEntries(Object.entries(linhas[i]!.inicios).filter(([x]) => percorridas(c, linhas[i]!.plano_slug, etapa).includes(x as Etapa))) });
  const pagaMensalidade = (l: LinhaNova) => Boolean(l.situacao === "ATIVO" && l.etapa && l.plataforma && c?.paga[l.etapa][l.plataforma]);
  const motivoDa = (l: LinhaNova) => c?.motivos.find((m) => m.codigo === l.motivo_encerramento);
  const completa = (l: LinhaNova) => Boolean(l.plano_slug && l.plataforma && l.situacao && l.etapa);
  const pronto = linhas.every(completa) && motivo.trim().length >= 5 && avisar !== "" && !salvando;

  const corpo = () => ({
    avisar: avisar === "sim",
    motivo: motivo.trim(),
    planos: linhas.map((l) => {
      const ativo = l.situacao === "ATIVO";
      return {
        plano_slug: l.plano_slug,
        plataforma: l.plataforma,
        comprada_em: l.comprada_em || null,
        situacao: l.situacao,
        etapa: l.etapa,
        inicios: Object.fromEntries(percorridas(c, l.plano_slug, l.etapa).map((x) => [x, l.inicios[x] || null])),
        subconta: l.subconta.trim() || null,
        mesa: l.mesa || null,
        confirmar_conta_em_uso: l.confirmar_conta_em_uso,
        ...(ativo && l.etapa === "TESTE" ? { teste_ate: l.teste_sem_prazo ? null : l.teste_ate || null, teste_sem_prazo: l.teste_sem_prazo } : {}),
        ...(pagaMensalidade(l) ? { proximo_vencimento: l.proximo_vencimento || null } : {}),
        ...(!ativo ? { encerrada_em: l.encerrada_em || null, motivo_encerramento: l.motivo_encerramento || null, detalhe_encerramento: l.detalhe_encerramento.trim() || null } : {}),
      };
    }),
  });
  const adicionar = async () => {
    setSalvando(true);
    setErro(undefined);
    try {
      const r = await postar<{ matriculas: string[]; email_enviado_para: string | null }>(`/v1/admin/pessoas/${id}/planos-antigos`, corpo());
      const n = r.matriculas.length;
      const feito = `${n} ${n === 1 ? "plano antigo adicionado" : "planos antigos adicionados"}. ${r.email_enviado_para ? `A pessoa recebeu um e-mail com todos em ${r.email_enviado_para}.` : "A pessoa não foi avisada."}`;
      navegar(`/traders/${id}`, { state: { feito } });
    } catch (e) {
      const corpoDoErro = e instanceof ErroApi ? (e.corpo as { linha?: number | null; codigo?: string } | undefined) : undefined;
      setErro({ texto: erroDe(e), linha: typeof corpoDoErro?.linha === "number" ? corpoDoErro.linha : null, contaEmUso: corpoDoErro?.codigo === CONTA_EM_USO });
    } finally {
      setSalvando(false);
    }
  };

  const estado = (i: number): EstadoDaLinha => (erro?.linha === i ? "erro" : undefined);
  const data = (rotulo: string, valor: string, aoMudar: (v: string) => void, extra: { max?: string; disabled?: boolean } = {}) => <EntradaDaPlanilha rotulo={rotulo} type="date" mono valor={valor} aoMudar={aoMudar} max={extra.max} disabled={extra.disabled} />;
  type L = LinhaNova & { i: number };

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Adicionar planos antigos"
        descricao={<>Para <LinkInterno className="mtc-link" href={`/traders/${id}`}>{nome}</LinkInterno>: os planos que a pessoa teve na plataforma antiga e não constam aqui. Cada linha é um plano, ativo ou encerrado. Nada é cobrado nem liberado por isso.</>}
      />
      {erro && erro.linha === null && <Aviso tipo="erro">{erro.texto}</Aviso>}
      {ctx.isLoading || pessoa.isLoading ? <Carregando compacto texto="Montando a planilha" /> : ctx.isError ? <Aviso tipo="erro">{erroDe(ctx.error)}</Aviso> : (
        <>
          <Planilha<L>
            rotulo="Planos antigos a adicionar"
            linhas={linhas.map((l, i) => ({ ...l, i }))}
            chave={(l) => l.chave}
            estado={(l) => estado(l.i)}
            colunas={[
              { chave: "n", rotulo: "", fixa: true, celula: (l) => <strong>Plano {l.i + 1}</strong> },
              {
                chave: "plano", rotulo: "Plano", celula: (l) => (
                  <SelecaoDaPlanilha rotulo={`Plano da linha ${l.i + 1}`} valor={l.plano_slug} aoMudar={(v) => trocarPlano(l.i, v)}
                    opcoes={[{ valor: "", rotulo: "Escolha o plano" }, ...c!.planos.map((p) => ({ valor: p.slug, rotulo: `${p.nome}${p.ativo ? "" : " (fora de venda)"}${p.etapas.length ? "" : " (sem família)"}` }))]} />
                ),
              },
              { chave: "plataforma", rotulo: "Plataforma", celula: (l) => <SelecaoDaPlanilha rotulo={`Plataforma da linha ${l.i + 1}`} valor={l.plataforma} aoMudar={(v) => mexer(l.i, { plataforma: v as Plataforma })} opcoes={[{ valor: "", rotulo: "Escolha" }, ...PLATAFORMAS]} /> },
              { chave: "compra", rotulo: "Compra (se souber)", celula: (l) => data(`Data da compra da linha ${l.i + 1}`, l.comprada_em, (v) => mexer(l.i, { comprada_em: v }), { max: c!.hoje }) },
              { chave: "situacao", rotulo: "Situação", celula: (l) => <SelecaoDaPlanilha rotulo={`Situação da linha ${l.i + 1}`} valor={l.situacao} aoMudar={(v) => mexer(l.i, { situacao: v as Situacao })} opcoes={[{ valor: "", rotulo: "Escolha" }, ...SITUACOES]} /> },
              {
                chave: "etapa", rotulo: "Etapa", celula: (l) => {
                  const etapas = c!.planos.find((p) => p.slug === l.plano_slug)?.etapas ?? [];
                  if (!l.plano_slug) return naoSeAplica;
                  if (!etapas.length) return <span className="mtc-planilha__apoio">Plano sem família</span>;
                  return <SelecaoDaPlanilha rotulo={`Etapa da linha ${l.i + 1}`} valor={l.etapa} aoMudar={(v) => trocarEtapa(l.i, v as Etapa)} opcoes={[{ valor: "", rotulo: l.situacao === "ENCERRADO" ? "Em que etapa parou" : "Em que etapa está" }, ...etapas.map((e) => ({ valor: e.codigo, rotulo: e.rotulo }))]} />;
                },
              },
              ...ETAPAS.map((x) => ({
                chave: `inicio-${x.codigo}`,
                rotulo: `Início ${x.rotulo}`,
                celula: (l: L) => (percorridas(c, l.plano_slug, l.etapa).includes(x.codigo) ? data(`Início de ${x.rotulo} da linha ${l.i + 1}`, l.inicios[x.codigo] ?? "", (v) => mexer(l.i, { inicios: { ...l.inicios, [x.codigo]: v } }), { max: c!.hoje }) : naoSeAplica),
              })),
              { chave: "subconta", rotulo: "Subconta", celula: (l) => <EntradaDaPlanilha rotulo={`Subconta da linha ${l.i + 1}`} mono valor={l.subconta} inputMode="numeric" aoMudar={(v) => mexer(l.i, { subconta: v, confirmar_conta_em_uso: false })} /> },
              { chave: "mesa", rotulo: "Mesa", celula: (l) => (l.etapa ? <SelecaoDaPlanilha rotulo={`Mesa da linha ${l.i + 1}`} valor={l.mesa} aoMudar={(v) => mexer(l.i, { mesa: v, confirmar_conta_em_uso: false })} opcoes={opcoesDeMesa(c, l.etapa)} /> : naoSeAplica) },
              {
                chave: "prazo", rotulo: "Prazo do teste", celula: (l) => (l.situacao === "ATIVO" && l.etapa === "TESTE" ? (
                  <div style={{ display: "grid", gap: "var(--e-1)" }}>
                    {data(`Prazo do teste da linha ${l.i + 1}`, l.teste_ate, (v) => mexer(l.i, { teste_ate: v }), { disabled: l.teste_sem_prazo })}
                    <label className="mtc-planilha__apoio" style={{ display: "flex", gap: "var(--e-1)", alignItems: "center" }}><input type="checkbox" checked={l.teste_sem_prazo} onChange={(e) => mexer(l.i, { teste_sem_prazo: e.target.checked })} /> sem prazo</label>
                  </div>
                ) : naoSeAplica),
              },
              { chave: "vencimento", rotulo: "Próximo vencimento", celula: (l) => (pagaMensalidade(l) ? data(`Próximo vencimento da linha ${l.i + 1}`, l.proximo_vencimento, (v) => mexer(l.i, { proximo_vencimento: v }), { }) : naoSeAplica) },
              { chave: "encerrado", rotulo: "Encerrado em", celula: (l) => (l.situacao === "ENCERRADO" ? data(`Data do encerramento da linha ${l.i + 1}`, l.encerrada_em, (v) => mexer(l.i, { encerrada_em: v }), { max: c!.hoje }) : naoSeAplica) },
              {
                chave: "motivo", rotulo: "Motivo do encerramento", celula: (l) => (l.situacao === "ENCERRADO" ? (
                  <div style={{ display: "grid", gap: "var(--e-1)" }}>
                    <SelecaoDaPlanilha rotulo={`Motivo do encerramento da linha ${l.i + 1}`} valor={l.motivo_encerramento} aoMudar={(v) => mexer(l.i, { motivo_encerramento: v })} opcoes={[{ valor: "", rotulo: "Escolha o motivo" }, ...c!.motivos.map((m) => ({ valor: m.codigo, rotulo: m.rotulo }))]} />
                    {motivoDa(l)?.exige_detalhe && <EntradaDaPlanilha rotulo={`Detalhe do encerramento da linha ${l.i + 1}`} valor={l.detalhe_encerramento} placeholder="Detalhe" aoMudar={(v) => mexer(l.i, { detalhe_encerramento: v })} />}
                  </div>
                ) : naoSeAplica),
              },
              {
                chave: "acoes", rotulo: "", celula: (l) => (
                  <div style={{ display: "grid", gap: "var(--e-1)", minWidth: 200 }}>
                    {linhas.length > 1 && <Botao tamanho="pequeno" variante="fantasma" onClick={() => { setErro(undefined); setLinhas((ls) => ls.filter((_, j) => j !== l.i)); }}>Tirar</Botao>}
                    {erro?.linha === l.i && (
                      <span className="mtc-planilha__apoio" role="alert" style={{ color: "var(--erro)", maxWidth: 300 }}>
                        {erro.texto}
                        {erro.contaEmUso && <label style={{ display: "flex", gap: "var(--e-1)", alignItems: "center", marginTop: "var(--e-1)", color: "var(--texto)" }}><input type="checkbox" checked={l.confirmar_conta_em_uso} onChange={(e) => mexer(l.i, { confirmar_conta_em_uso: e.target.checked })} /> Confirmo a subconta</label>}
                      </span>
                    )}
                  </div>
                ),
              },
            ]}
          />
          <div><Botao variante="secundario" disabled={linhas.length >= 20} onClick={() => setLinhas((ls) => [...ls, linhaVazia()])}>Adicionar linha</Botao></div>
          <div style={{ maxWidth: 640, display: "grid", gap: "var(--e-4)" }}>
            <CampoTextoLongo rotulo="Por que está adicionando" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: planos da plataforma antiga que não vieram na troca, conferidos na extranet." dica="Vale pra todos os planos desta vez. Fica na trilha, com o seu nome." />
            <Segmentado rotulo="Avisar o cliente sobre estes planos?" nome="avisar" valor={avisar} aoMudar={(v) => setAvisar(v)} opcoes={[{ valor: "sim", rotulo: "Sim" }, { valor: "nao", rotulo: "Não" }]} />
            <span className="mtc-campo__dica">{avisar === "sim" ? `Um e-mail só, com todos os planos desta vez, e um aviso na área de ${nome}.` : avisar === "nao" ? "Nada sai pro cliente: nem e-mail, nem aviso na área." : "Vale pra todos os planos desta vez."}</span>
            <div className="mtc-linha" style={{ gap: "var(--e-3)" }}>
              <Botao disabled={!pronto} carregando={salvando} onClick={() => void adicionar()}>{linhas.length === 1 ? "Adicionar 1 plano" : `Adicionar ${linhas.length} planos`}</Botao>
              <Botao variante="fantasma" onClick={() => navegar(`/traders/${id}`)}>Cancelar</Botao>
            </div>
          </div>
        </>
      )}
    </Pilha>
  );
}

// ------------------------------------------------------------------ lista (Cadastros > Planos antigos)

export type PlanoAntigo = {
  id: string;
  pessoa: { id: string; nome: string; cpf: string };
  plano: { slug: string; nome: string };
  situacao: Situacao;
  plataforma: Plataforma | null;
  comprada_em: string;
  etapa: Etapa | null;
  inicios: Inicios;
  subconta: string | null;
  mesa: string | null;
  encerrada_em: string | null;
  motivo_encerramento: string | null;
  detalhe_encerramento: string | null;
};
type Rascunho = Partial<Pick<PlanoAntigo, "plataforma" | "comprada_em" | "etapa" | "subconta" | "mesa" | "encerrada_em" | "motivo_encerramento" | "detalhe_encerramento">> & { inicios?: Inicios; confirmar?: boolean };

/** O corpo da correcao: so o que mudou vai pro backend. */
function corpoDaCorrecao(l: PlanoAntigo, d: Rascunho) {
  const inicios = Object.fromEntries(Object.entries(d.inicios ?? {}).filter(([x, v]) => v && v !== l.inicios[x as Etapa]));
  const campos = (["plataforma", "comprada_em", "etapa", "subconta", "mesa", "encerrada_em", "motivo_encerramento", "detalhe_encerramento"] as const).filter((k) => d[k] !== undefined && d[k] !== l[k]);
  return { ...Object.fromEntries(campos.map((k) => [k, d[k] ?? ""])), ...(Object.keys(inicios).length ? { inicios } : {}), ...(d.confirmar ? { confirmar_conta_em_uso: true } : {}) };
}

/** A recusa da correcao como a tela mostra: subconta em uso pede confirmacao; virar pagante e pelo Gerenciar plano. */
function recusaDaCorrecao(e: unknown) {
  const codigo = e instanceof ErroApi ? (e.corpo as { codigo?: string } | undefined)?.codigo : undefined;
  const gerenciar = codigo === "DECIDIR_MENSALIDADE";
  return { texto: gerenciar ? "Essa mudança faz o plano passar a pagar mensalidade. Faça pelo Gerenciar plano, que pergunta o que fazer com a primeira." : erroDe(e), contaEmUso: codigo === CONTA_EM_USO, gerenciar };
}

/** Os planos antigos de um cliente, pra ficha dele editar e excluir (057, secao 4). */
export function usePlanosAntigosDoCliente(pessoaId: string, ligado: boolean) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["planos-antigos", "cliente", pessoaId], queryFn: () => ler<{ itens: PlanoAntigo[] }>(`/v1/admin/planos-antigos?pessoa_id=${encodeURIComponent(pessoaId)}`), enabled: ligado });
}
/** Lista e ficha leem o mesmo plano: mudou num, os dois buscam de novo. */
const recarregar = (qc: ReturnType<typeof useQueryClient>) => Promise.all([qc.invalidateQueries({ queryKey: ["planos-antigos", "lista"] }), qc.invalidateQueries({ queryKey: ["planos-antigos", "cliente"] })]);

export function PaginaPlanosAntigos() {
  const { ler, mudar } = useApi();
  const qc = useQueryClient();
  const ctx = useContexto();
  const q = useQuery({ queryKey: ["planos-antigos", "lista"], queryFn: () => ler<{ itens: PlanoAntigo[] }>("/v1/admin/planos-antigos") });
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [erros, setErros] = useState<Record<string, { texto: string; contaEmUso: boolean; gerenciar: boolean }>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [feito, setFeito] = useState<string>();
  const [busca, setBusca] = useState("");
  const [excluindo, setExcluindo] = useState<{ id: string; nome: string } | null>(null);
  const pag = usePagina("pagina", [busca]);
  const c = ctx.data;

  const r = (l: PlanoAntigo) => rascunhos[l.id] ?? {};
  const valor = <K extends keyof Rascunho & keyof PlanoAntigo>(l: PlanoAntigo, k: K) => (r(l)[k] !== undefined ? r(l)[k] : l[k]) as PlanoAntigo[K];
  const etapa = (l: PlanoAntigo) => valor(l, "etapa");
  const inicio = (l: PlanoAntigo, x: Etapa) => r(l).inicios?.[x] ?? l.inicios[x] ?? "";
  const mexer = (l: PlanoAntigo, p: Rascunho) => {
    setFeito(undefined);
    setErros(({ [l.id]: _, ...e }) => e);
    setRascunhos((t) => ({ ...t, [l.id]: { ...t[l.id], ...p } }));
  };
  const desfazer = (l: PlanoAntigo) => { setRascunhos(({ [l.id]: _, ...t }) => t); setErros(({ [l.id]: _, ...e }) => e); };
  const salvar = async (l: PlanoAntigo) => {
    setSalvando(l.id);
    try {
      await mudar(`/v1/admin/planos-antigos/${l.id}`, corpoDaCorrecao(l, r(l)));
      await recarregar(qc);
      desfazer(l);
      setFeito(`${l.plano.nome} de ${l.pessoa.nome} salvo. O cliente não foi avisado.`);
    } catch (e) {
      setErros((t) => ({ ...t, [l.id]: recusaDaCorrecao(e) }));
    } finally {
      setSalvando(null);
    }
  };

  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const linhas = (q.data?.itens ?? []).filter((l) => !termo || l.pessoa.nome.toLowerCase().includes(termo) || l.plano.nome.toLowerCase().includes(termo) || (digitos.length >= 3 && (l.pessoa.cpf.replace(/\D/g, "").includes(digitos) || (l.subconta ?? "").includes(digitos))));
  const estado = (l: PlanoAntigo): EstadoDaLinha => (erros[l.id] ? "erro" : rascunhos[l.id] ? "mudou" : undefined);
  const mudou = (l: PlanoAntigo, k: keyof Rascunho) => r(l)[k] !== undefined;
  const data = (l: PlanoAntigo, rotulo: string, v: string, aoMudar: (v: string) => void, m: boolean) => <EntradaDaPlanilha rotulo={`${rotulo} de ${l.pessoa.nome}`} type="date" mono valor={v} mudou={m} max={c?.hoje} aoMudar={aoMudar} />;

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Planos antigos" descricao="Os planos da plataforma antiga adicionados pela ficha do cliente. Corrija direto na planilha: o cliente não é avisado e a trilha guarda o antes e o depois. O plano e a situação mudam pela ficha (Gerenciar plano, Encerrar)." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <div style={{ maxWidth: 360 }}><Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, plano ou subconta" /></div>
      {q.isLoading || ctx.isLoading ? <Carregando compacto texto="Buscando os planos antigos" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <>
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{linhas.length} de {q.data!.itens.length} {q.data!.itens.length === 1 ? "plano" : "planos"}. {(() => { const n = q.data!.itens.filter((l) => l.situacao === "ATIVO").length; return `${n} ${n === 1 ? "ativo" : "ativos"}.`; })()}</span>
          <ForaDaPagina linhas={linhas} alteradas={(l) => Boolean(rascunhos[l.id])} nome={(l) => `${l.plano.nome} de ${l.pessoa.nome}`} pagina={pag.pagina} aoIr={pag.aoMudar} />
          <Planilha
            rotulo="Planos antigos"
            linhas={linhas}
            paginacao={pag}
            chave={(l) => l.id}
            estado={estado}
            vazio={<Vazio titulo={termo ? "Nada com essa busca" : "Nenhum plano antigo"}>{termo ? "Confira o nome, o CPF, o plano ou a subconta." : "Adicione pela ficha do cliente: Adicionar plano, Vários planos antigos."}</Vazio>}
            colunas={[
              { chave: "nome", rotulo: "Cliente", fixa: true, celula: (l) => <><LinkInterno className="mtc-link" href={`/traders/${l.pessoa.id}`}><strong>{l.pessoa.nome}</strong></LinkInterno><span className="mtc-planilha__apoio mtc-mono">{l.pessoa.cpf}</span></> },
              { chave: "plano", rotulo: "Plano", celula: (l) => <><LinkInterno className="mtc-link" href={`/matriculas/${l.id}`}>{l.plano.nome}</LinkInterno><span className="mtc-planilha__apoio"><Selo tom={l.situacao === "ATIVO" ? "sucesso" : "neutro"}>{l.situacao === "ATIVO" ? "Ativo" : "Encerrado"}</Selo></span></> },
              { chave: "plataforma", rotulo: "Plataforma", celula: (l) => <SelecaoDaPlanilha rotulo={`Plataforma de ${l.pessoa.nome}`} valor={valor(l, "plataforma") ?? ""} mudou={mudou(l, "plataforma")} aoMudar={(v) => mexer(l, { plataforma: v as Plataforma })} opcoes={PLATAFORMAS} /> },
              { chave: "compra", rotulo: "Compra", celula: (l) => data(l, "Data da compra", valor(l, "comprada_em"), (v) => mexer(l, { comprada_em: v }), mudou(l, "comprada_em")) },
              {
                chave: "etapa", rotulo: "Etapa", celula: (l) => {
                  const etapas = c?.planos.find((p) => p.slug === l.plano.slug)?.etapas ?? [];
                  return <SelecaoDaPlanilha rotulo={`Etapa de ${l.pessoa.nome}`} valor={etapa(l) ?? ""} mudou={mudou(l, "etapa")} aoMudar={(v) => mexer(l, { etapa: v as Etapa, mesa: "" })} opcoes={etapas.map((e) => ({ valor: e.codigo, rotulo: e.rotulo }))} />;
                },
              },
              ...ETAPAS.map((x) => ({
                chave: `inicio-${x.codigo}`,
                rotulo: `Início ${x.rotulo}`,
                celula: (l: PlanoAntigo) => (percorridas(c, l.plano.slug, etapa(l)).includes(x.codigo) ? data(l, `Início de ${x.rotulo}`, inicio(l, x.codigo), (v) => mexer(l, { inicios: { ...r(l).inicios, [x.codigo]: v } }), r(l).inicios?.[x.codigo] !== undefined) : naoSeAplica),
              })),
              { chave: "subconta", rotulo: "Subconta", celula: (l) => <EntradaDaPlanilha rotulo={`Subconta de ${l.pessoa.nome}`} mono inputMode="numeric" valor={valor(l, "subconta") ?? ""} mudou={mudou(l, "subconta")} aoMudar={(v) => mexer(l, { subconta: v })} /> },
              { chave: "mesa", rotulo: "Mesa", celula: (l) => <SelecaoDaPlanilha rotulo={`Mesa de ${l.pessoa.nome}`} valor={valor(l, "mesa") ?? ""} mudou={mudou(l, "mesa")} aoMudar={(v) => mexer(l, { mesa: v })} opcoes={opcoesDeMesa(c, etapa(l))} /> },
              { chave: "encerrado", rotulo: "Encerrado em", celula: (l) => (l.situacao === "ENCERRADO" ? data(l, "Data do encerramento", valor(l, "encerrada_em") ?? "", (v) => mexer(l, { encerrada_em: v }), mudou(l, "encerrada_em")) : naoSeAplica) },
              {
                chave: "motivo", rotulo: "Motivo do encerramento", celula: (l) => {
                  if (l.situacao !== "ENCERRADO") return naoSeAplica;
                  const m = c?.motivos.find((x) => x.codigo === valor(l, "motivo_encerramento"));
                  return (
                    <div style={{ display: "grid", gap: "var(--e-1)" }}>
                      <SelecaoDaPlanilha rotulo={`Motivo do encerramento de ${l.pessoa.nome}`} valor={valor(l, "motivo_encerramento") ?? ""} mudou={mudou(l, "motivo_encerramento")} aoMudar={(v) => mexer(l, { motivo_encerramento: v })} opcoes={(c?.motivos ?? []).map((x) => ({ valor: x.codigo, rotulo: x.rotulo }))} />
                      {m?.exige_detalhe && <EntradaDaPlanilha rotulo={`Detalhe do encerramento de ${l.pessoa.nome}`} valor={valor(l, "detalhe_encerramento") ?? ""} mudou={mudou(l, "detalhe_encerramento")} placeholder="Detalhe" aoMudar={(v) => mexer(l, { detalhe_encerramento: v })} />}
                    </div>
                  );
                },
              },
              {
                chave: "acoes", rotulo: "", celula: (l) => (
                  <div style={{ display: "grid", gap: "var(--e-1)", minWidth: 200 }}>
                    {rascunhos[l.id] ? (
                      <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
                        <Botao tamanho="pequeno" disabled={salvando !== null} carregando={salvando === l.id} onClick={() => void salvar(l)}>Salvar</Botao>
                        <Botao tamanho="pequeno" variante="fantasma" disabled={salvando === l.id} onClick={() => desfazer(l)}>Desfazer</Botao>
                      </div>
                    ) : <Botao tamanho="pequeno" variante="fantasma" aria-label={`Excluir ${l.plano.nome} de ${l.pessoa.nome}`} onClick={() => { setFeito(undefined); setExcluindo({ id: l.id, nome: `${l.plano.nome} de ${l.pessoa.nome}` }); }}>Excluir</Botao>}
                    {erros[l.id] && (
                      <span className="mtc-planilha__apoio" role="alert" style={{ color: "var(--erro)", maxWidth: 300 }}>
                        {erros[l.id]!.texto}
                        {erros[l.id]!.gerenciar && <> <LinkInterno className="mtc-link" href={`/matriculas/${l.id}`}>Abrir o plano</LinkInterno></>}
                        {erros[l.id]!.contaEmUso && <label style={{ display: "flex", gap: "var(--e-1)", alignItems: "center", marginTop: "var(--e-1)", color: "var(--texto)" }}><input type="checkbox" checked={Boolean(r(l).confirmar)} onChange={(e) => setRascunhos((t) => ({ ...t, [l.id]: { ...t[l.id], confirmar: e.target.checked } }))} /> Confirmo a subconta</label>}
                      </span>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
      <ExcluirPlanoAntigo plano={excluindo} aoFechar={() => setExcluindo(null)} aoExcluido={setFeito} />
    </Pilha>
  );
}

// ------------------------------------------------------------------ editar e excluir (lista e ficha do cliente)

/**
 * Corrige um plano antigo num dialogo (ficha do cliente; Vitor, 23/10: "botão de editar e excluir"): os campos e a rota
 * da lista, sem avisar o cliente. Plano e situacao nao mudam aqui (057, 2.4): errados, exclui e adiciona de novo.
 */
export function EditarPlanoAntigo({ plano: l, aoFechar, aoSalvo }: { plano: PlanoAntigo | null; aoFechar: () => void; aoSalvo: (texto: string) => void }) {
  const { mudar } = useApi();
  const qc = useQueryClient();
  const ctx = useContexto(Boolean(l));
  const c = ctx.data;
  const [d, setD] = useState<Rascunho>({});
  const [recusa, setRecusa] = useState<{ contaEmUso: boolean; gerenciar: boolean }>({ contaEmUso: false, gerenciar: false });
  const mexer = (p: Rascunho) => setD((x) => ({ ...x, ...p, ...("subconta" in p || "mesa" in p || "etapa" in p ? { confirmar: false } : {}) }));
  const valor = <K extends keyof Rascunho & keyof PlanoAntigo>(k: K) => (d[k] !== undefined ? d[k] : l?.[k]) as PlanoAntigo[K];
  const etapa = valor("etapa");
  const motivo = c?.motivos.find((x) => x.codigo === valor("motivo_encerramento"));
  const corpo = l ? corpoDaCorrecao(l, d) : {};
  const enviar = async () => {
    try {
      await mudar(`/v1/admin/planos-antigos/${l!.id}`, corpo);
    } catch (e) {
      const r = recusaDaCorrecao(e);
      setRecusa(r);
      throw e instanceof ErroApi ? new ErroApi(e.status, { ...e.corpo, erro: r.texto }, e.codigo) : e;
    }
  };
  const nome = l ? `${l.plano.nome} de ${l.pessoa.nome}` : "";
  return (
    <DialogoDeAcao
      aberto={Boolean(l)}
      aoFechar={aoFechar}
      titulo={`Editar ${l?.plano.nome ?? "plano"}`}
      descricao={l ? `${l.situacao === "ATIVO" ? "Ativo" : "Encerrado"}, da plataforma antiga. O cliente não é avisado e a trilha guarda o antes e o depois. Plano ou situação errados: exclua e adicione de novo.` : undefined}
      botao="Salvar"
      podeEnviar={Object.keys(corpo).length > 0}
      aoAbrir={() => { setD({}); setRecusa({ contaEmUso: false, gerenciar: false }); }}
      aoEnviar={enviar}
      aoConcluir={() => { void recarregar(qc); aoSalvo(`${nome} salvo. O cliente não foi avisado.`); }}
    >
      {!l ? null : ctx.isError ? <Aviso tipo="erro">{erroDe(ctx.error)}</Aviso> : !c ? <Carregando compacto texto="Buscando as etapas e as mesas" /> : (
        <>
          {recusa.gerenciar && <LinkInterno className="mtc-link" href={`/matriculas/${l.id}`}>Abrir o plano</LinkInterno>}
          <div style={{ display: "grid", gap: "var(--e-4)", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
            <CampoSelecao rotulo="Plataforma" valor={valor("plataforma") ?? ""} vazio="Escolha" required aoMudar={(v) => mexer({ plataforma: v })} opcoes={PLATAFORMAS} />
            <CampoData rotulo="Data da compra" valor={valor("comprada_em")} max={c.hoje} aoMudar={(v) => mexer({ comprada_em: v })} />
            <CampoSelecao rotulo={l.situacao === "ENCERRADO" ? "Etapa em que parou" : "Etapa"} valor={etapa ?? ""} aoMudar={(v) => mexer({ etapa: v, mesa: "" })} opcoes={(c.planos.find((p) => p.slug === l.plano.slug)?.etapas ?? []).map((e) => ({ valor: e.codigo, rotulo: e.rotulo }))} />
            {percorridas(c, l.plano.slug, etapa).map((x) => (
              <CampoData key={x} rotulo={`Início de ${ETAPAS.find((e) => e.codigo === x)!.rotulo}`} valor={d.inicios?.[x] ?? l.inicios[x] ?? ""} max={c.hoje} aoMudar={(v) => mexer({ inicios: { ...d.inicios, [x]: v } })} />
            ))}
            <Campo rotulo="Subconta" mono inputMode="numeric" valor={valor("subconta") ?? ""} aoMudar={(v) => mexer({ subconta: v })} />
            <CampoSelecao rotulo="Mesa" valor={valor("mesa") ?? ""} aoMudar={(v) => mexer({ mesa: v })} opcoes={opcoesDeMesa(c, etapa)} />
            {l.situacao === "ENCERRADO" && (
              <>
                <CampoData rotulo="Encerrado em" valor={valor("encerrada_em") ?? ""} max={c.hoje} aoMudar={(v) => mexer({ encerrada_em: v })} />
                <CampoSelecao rotulo="Motivo do encerramento" valor={valor("motivo_encerramento") ?? ""} aoMudar={(v) => mexer({ motivo_encerramento: v })} opcoes={c.motivos.map((x) => ({ valor: x.codigo, rotulo: x.rotulo }))} />
                {motivo?.exige_detalhe && <Campo rotulo="Detalhe do encerramento" valor={valor("detalhe_encerramento") ?? ""} aoMudar={(v) => mexer({ detalhe_encerramento: v })} />}
              </>
            )}
          </div>
          {recusa.contaEmUso && <label style={{ display: "flex", gap: "var(--e-2)", alignItems: "center" }}><input type="checkbox" checked={Boolean(d.confirmar)} onChange={(e) => setD((x) => ({ ...x, confirmar: e.target.checked }))} /> Confirmo a subconta</label>}
        </>
      )}
    </DialogoDeAcao>
  );
}

/** Exclui um plano antigo adicionado por engano (057, 2.5), da lista ou da ficha do cliente. `nome`: "Plano de Fulano". */
export function ExcluirPlanoAntigo({ plano, aoFechar, aoExcluido }: { plano: { id: string; nome: string } | null; aoFechar: () => void; aoExcluido: (texto: string) => void }) {
  const { apagar } = useApi();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  return (
    <DialogoDeAcao
      aberto={Boolean(plano)}
      aoFechar={aoFechar}
      titulo={plano ? `Excluir ${plano.nome}?` : ""}
      descricao="Só pra plano adicionado por engano. Com repasse, mensalidade paga ou pedido ligado, não exclui: corrija o plano em vez disso."
      botao="Excluir plano"
      variante="perigo"
      podeEnviar={motivo.trim().length >= 5}
      aoAbrir={() => setMotivo("")}
      aoEnviar={() => apagar(`/v1/admin/planos-antigos/${plano!.id}`, { motivo: motivo.trim() })}
      aoConcluir={() => { void recarregar(qc); aoExcluido(`${plano!.nome} excluído. Fica na trilha do cliente.`); }}
    >
      <CampoTextoLongo rotulo="Por que está excluindo" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: adicionado no cliente errado." />
    </DialogoDeAcao>
  );
}
